import {
  Avatar,
  Button,
  CircularProgress,
  InputLabel,
  SelectChangeEvent,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import { ONE_DAY_OF_MS } from "../../utils/timeUtils";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { Process, TimeRange, WorkScheduleConfigJsonType } from "types/types";
import { useGetWorkScheduleConfigList } from "components/DataGetter/WorkSchedulesGetter";
import { useWorkScheduleConfigJsonListAtom } from "store/atoms";
import { useSelector } from "store/useSelector";
import {
  useEffectOnce,
  useFocusRange,
  useWorkScheduleIdOnCurrentPage,
} from "utils/customHooks/genericHooks";
import { uniqBy } from "lodash";
import LoadingCircle from "reports/components/simple/LoadingCircle";
import { Download } from "@mui/icons-material";
import { DateRangePickerMode } from "components/Dashboard/range-selectors/RangeSelectorToggleable/DateRangePickerMode/DateRangePickerMode";
import { useLocation, useNavigate } from "react-router";
import { ProcessesPicker } from "pages/SnapshotsPage/ProcessesPicker";
import { ProcessGroupsPicker } from "pages/SnapshotsPage/ProcessGroupsPicker";
import { BREAKPOINT_TABLET } from "constants/constants";
import { GenerateCSVButtonGroup, getFileName } from "./GenerateCSVButtonGroup";
import { setFocusRange } from "providers/GlobalStateProvider";
import { useDispatch } from "react-redux";

export default function DataDumpsDownloaderPage() {
  // wait for processes to load
  const allGroups = useSelector(s => s.legacy.allGroups);
  const ready = allGroups.length > 0;
  return ready ? (
    <DataDumpsDownloaderPageContent />
  ) : (
    <div
      style={{
        position: "fixed",
        left: "50vw",
        right: "50vw",
        top: "40vh",
      }}
    >
      <CircularProgress />
    </div>
  );
}
function DataDumpsDownloaderPageContent() {
  const ckey = useCustomerKey();
  const now = useRef(Date.now()).current;
  const focusRange = useFocusRange();
  // Time range dropdown data
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>({
    ge: focusRange?.ge ?? now - ONE_DAY_OF_MS,
    lt: focusRange?.lt ?? now,
  });
  useEffect(() => {
    if (focusRange) {
      setSelectedTimeRange(focusRange);
    }
  }, [focusRange]);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const resetSubmit = () => setHasSubmitted(false);

  // Work schedule config dropdown data
  useGetWorkScheduleConfigList();
  const [workScheduleConfigList] = useWorkScheduleConfigJsonListAtom();
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const workScheduleConfigListItems =
    workScheduleConfigList?.slice().sort((a, b) => a.id.localeCompare(b.id)) ||
    [];

  const [selectedWorkScheduleConfig, setSelectedWorkScheduleConfig] =
    useState<WorkScheduleConfigJsonType | null>(
      workScheduleConfigList.find(({ id }) => id === workScheduleId) || null
    );

  // sync workSchedule in the url
  const navigate = useNavigate();
  useEffect(() => {
    const workScheduleParam = new URLSearchParams(window.location.search).get(
      "workSchedule"
    );
    if (
      workScheduleParam &&
      workScheduleParam !== selectedWorkScheduleConfig?.id
    ) {
      navigate(`?workSchedule=${selectedWorkScheduleConfig?.id}`, {
        replace: true,
      });
    }
  }, [selectedWorkScheduleConfig, navigate]);

  // set focusRange from url params once if it's null
  const dispatch = useDispatch();
  useEffectOnce({
    callback: () => {
      const ge = urlSearchParams.get("ge");
      const lt = urlSearchParams.get("lt");
      if (ge && lt) {
        dispatch(setFocusRange({ ge: Number(ge), lt: Number(lt) }));
      }
    },
    shouldRun: !focusRange,
    dependencies: [focusRange],
  });

  const handleWorkScheduleConfigChange = (event: SelectChangeEvent) => {
    setSelectedProcesses([]);
    setSelectedGroups([]);
    const selectedId = event.target.value;
    const workScheduleConfig =
      workScheduleConfigListItems?.find(({ id }) => id === selectedId) || null;
    setSelectedWorkScheduleConfig(workScheduleConfig);
  };

  const allGroups = useSelector(s => s.legacy.allGroups);

  const allProcesses = allGroups.reduce(
    (acc, group) => [...acc, ...group.processes],
    [] as Process[]
  );
  const processList = uniqBy(allProcesses, p => p.id);

  const allProcessesLabeled = allProcesses.map(p => ({
    label: p.name,
    id: p.id,
  }));

  const allGroupsLabeled = allGroups.map(pg => ({
    label: pg.shortName ?? pg.longName,
    id: pg.id ?? pg.shortName ?? pg.longName,
    processIds: pg.processes.map(p => p.id),
  }));

  // sync selected processes + groups to url params pids, pgids
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);

  const initialSelectedProcessIds =
    urlSearchParams.get("pids")?.split(",") ?? [];
  const initialSelectedProcesses = allProcessesLabeled.filter(p =>
    initialSelectedProcessIds.includes(p.id)
  );

  const [selectedProcesses, setSelectedProcesses] = useState<
    {
      label: string;
      id: string;
    }[]
  >(initialSelectedProcesses);

  const initialSelectedProcessGroupIds =
    urlSearchParams.get("pgids")?.split(",") ?? [];
  const initialSelectedProcessGroups = allGroupsLabeled.filter(pg =>
    initialSelectedProcessGroupIds.includes(pg.id)
  );

  const [selectedGroups, setSelectedGroups] = useState<
    {
      label: string;
      id: string;
      processIds: string[];
    }[]
  >(initialSelectedProcessGroups);

  // sync url params pids, pgids when we change state
  useEffect(() => {
    const selectedProcessIds = selectedProcesses.map(p => p.id);
    const selectedProcessGroupIds = selectedGroups.map(pg => pg.id);
    const newSearchParams = new URLSearchParams(location.search);
    if (selectedProcessIds.length) {
      newSearchParams.set("pids", selectedProcessIds.join(","));
    } else {
      newSearchParams.delete("pids");
    }
    if (selectedProcessGroupIds.length) {
      newSearchParams.set("pgids", selectedProcessGroupIds.join(","));
    } else {
      newSearchParams.delete("pgids");
    }
    if (selectedProcessIds.length || selectedProcessGroupIds.length) {
      window.history.replaceState(
        {},
        "",
        `${location.pathname}?${newSearchParams}`
      );
    }
  }, [selectedProcesses, selectedGroups, location.search, location.pathname]);

  const machemetryIdList =
    selectedProcesses.length > 0 ? selectedProcesses.map(({ id }) => id) : [];

  // if we have only one workscheduleconfig item and it's not selected, select it
  useEffectOnce({
    callback: () => {
      handleWorkScheduleConfigChange({
        target: { value: workScheduleConfigListItems[0]?.id || "" },
      } as SelectChangeEvent);
    },
    shouldRun:
      workScheduleConfigListItems.length === 1 &&
      (!selectedWorkScheduleConfig ||
        (workScheduleConfigListItems[0] &&
          selectedWorkScheduleConfig.id !==
            workScheduleConfigListItems[0]?.id)),
    dependencies: [workScheduleConfigListItems],
  });

  const generateCsvDisabled = Boolean(
    selectedProcesses.length === 0 ||
      (focusRange && focusRange.ge >= focusRange.lt)
  );
  return !allGroups || !processList || processList.length <= 0 ? (
    <LoadingCircle />
  ) : (
    <DataDumpsDownloaderPageStyles>
      <div className="card cardItem">
        <PageHeaderStyles>
          <div className="flexRow">
            <div className="leftSide">
              <Avatar>
                <Download />
              </Avatar>
            </div>
            <div className="rightSide">
              <h1>Data Dumps</h1>
              <p>Generate CSV of Data Dumps</p>
            </div>
          </div>
        </PageHeaderStyles>
        <div className="dataDumpDownloader">
          <DateRangePickerMode
            autoSubmit={true}
            onSubmit={() => {
              const didTimeRangeChange =
                focusRange?.ge !== selectedTimeRange.ge ||
                focusRange?.lt !== selectedTimeRange.lt;
              if (didTimeRangeChange) {
                resetSubmit();
              }
            }}
          />
          <div className="pickers">
            <ProcessesPicker
              {...{
                allProcessesLabeled,
                selectedProcesses,
                setSelectedProcesses,
                onSelectProcess: () => {
                  setSelectedGroups([]);
                },
              }}
            />
            <ProcessGroupsPicker
              {...{
                allProcessesLabeled,
                allGroupsLabeled,
                selectedGroups,
                setSelectedGroups,
                selectedProcesses,
                setSelectedProcesses,
              }}
            />
          </div>
          {selectedWorkScheduleConfig ? (
            <div className="actions">
              <GenerateCSVButtonGroup
                range={selectedTimeRange}
                fileName={getFileName(ckey, "", selectedTimeRange)}
                hasSubmitted={hasSubmitted}
                onClick={() => setHasSubmitted(true)}
                machemetryIds={machemetryIdList}
                disabled={generateCsvDisabled}
              />
              <Button onClick={resetSubmit} disabled={!hasSubmitted}>
                Cancel
              </Button>
            </div>
          ) : (
            <InputLabel error={true}>
              Please select the Work Schedule Config to download the datadump
            </InputLabel>
          )}
        </div>
      </div>
    </DataDumpsDownloaderPageStyles>
  );
}
const DataDumpsDownloaderPageStyles = styled.div`
  .card {
    margin: 2em auto;
    padding: 2em;
    display: flex;
    gap: 1em;
    flex-direction: column;
    justify-content: center;
    align-items: baseline;
    max-width: min(calc(100vw - 32px), 600px);
  }
  &&& .rightSide {
    padding-right: 0;
  }
  .dataDumpDownloader {
    display: flex;
    flex-direction: column;
    gap: 2em;
    width: 100%;
    align-items: center;
  }
  .pickers {
    display: flex;
    flex-direction: column;
    gap: 1em;
    width: 100%;
    box-sizing: border-box;
  }

  .actions {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 1em;
    justify-content: center;
    margin-top: 1em;
  }
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    .pickers {
      padding: 0 1.5em;
    }
    .actions {
      .generateDownloadGroup {
        width: 400px;
        .MuiButtonGroup-firstButton {
          flex-grow: 1;
        }
      }
    }
  }
  @media (max-width: ${BREAKPOINT_TABLET}px) {
    .btnBack,
    .btnForward {
      display: none;
    }
    .picker-and-controls,
    .react-datetimerange-picker {
      margin-left: 0;
    }
  }
`;

const PageHeaderStyles = styled.div`
  padding: 1em;
  .flexRow {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    .leftSide {
      width: 72px;
      display: flex;
      justify-content: center;
      align-items: flex-start;

      .MuiAvatar-root {
        width: 80px;
        height: 80px;

        &.admin {
          background: transparent;
        }

        .MuiSvgIcon-root {
          width: 75%;
          height: 75%;
        }
      }
      padding: 0.5em;
    }

    .rightSide {
      padding: 1em;
    }
  }

  p {
    padding: 0.25em 0;
    font-size: 1.5em;
    color: rgba(0, 0, 0, 0.5);
  }
`;
